import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Role } from '../models/models';
import api from './api';

interface RoleState {
  roleData: Role[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedRole: Role | null;
  rolePermissions: number[]; // IDs des permissions du rôle sélectionné
}

const initialState: RoleState = {
  roleData: [],
  total: 0,
  totalPages: 1,
  currentPage: 1,
  loading: false,
  error: null,
  successMessage: null,
  selectedRole: null,
  rolePermissions: [],
};

export const fetchRoles = createAsyncThunk<
  { data: Role[]; total: number; totalPages: number; currentPage: number },
  { limit?: number; offset?: number },
  { rejectValue: string }
>('role/fetchAll', async (params, thunkAPI) => {
  try {
    const response = await api.get('/roles', { params });
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement rôles.');
  }
});

export const saveRole = createAsyncThunk<Role, Role, { rejectValue: string }>(
  'role/save',
  async (role, thunkAPI) => {
    try {
      const response = await api.post('/roles', role);
      return response.data as Role;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur sauvegarde rôle.');
    }
  }
);

export const deleteRole = createAsyncThunk<void, number, { rejectValue: string }>(
  'role/delete',
  async (id, thunkAPI) => {
    try {
      await api.delete('/roles', { params: { id } });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur suppression rôle.');
    }
  }
);

export const fetchRolePermissions = createAsyncThunk<number[], number, { rejectValue: string }>(
  'role/fetchPermissions',
  async (role_id, thunkAPI) => {
    try {
      const response = await api.get(`/roles/${role_id}/permissions`);
      return (response.data.data as { id: number }[]).map(p => p.id);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur chargement permissions.');
    }
  }
);

export const assignPermissionsToRole = createAsyncThunk<
  void,
  { role_id: number; permission_ids: number[] },
  { rejectValue: string }
>('role/assignPermissions', async ({ role_id, permission_ids }, thunkAPI) => {
  try {
    await api.post(`/roles/${role_id}/permissions`, { permission_ids });
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Erreur assignation permissions.');
  }
});

const roleSlice = createSlice({
  name: 'RoleSlice',
  initialState,
  reducers: {
    setSelectedRole(state, action: PayloadAction<Role | null>) {
      state.selectedRole = action.payload;
    },
    clearRoleData(state) {
      state.roleData = [];
      state.total = 0;
      state.totalPages = 1;
      state.currentPage = 1;
    },
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roleData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchRoles.rejected, (state, action) => { state.loading = false; state.error = action.payload || 'Erreur'; })
      .addCase(saveRole.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(saveRole.fulfilled, (state) => { state.loading = false; state.successMessage = 'Rôle enregistré.'; })
      .addCase(saveRole.rejected, (state, action) => { state.loading = false; state.error = action.payload || 'Erreur'; })
      .addCase(deleteRole.fulfilled, (state) => { state.successMessage = 'Rôle supprimé.'; })
      .addCase(deleteRole.rejected, (state, action) => { state.error = action.payload || 'Erreur'; })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => { state.rolePermissions = action.payload; })
      .addCase(assignPermissionsToRole.fulfilled, (state) => { state.successMessage = 'Permissions mises à jour.'; })
      .addCase(assignPermissionsToRole.rejected, (state, action) => { state.error = action.payload || 'Erreur'; });
  },
});

export const { setSelectedRole, clearRoleData, clearMessages: clearRoleMessages } = roleSlice.actions;
export default roleSlice.reducer;
